import { Box, Chip, Dialog, DialogContent, DialogTitle, MenuItem, TextField, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { useState } from "react";

import { DataTable, DateText, PageHeader, useListParams } from "../components";
import help from "./HELP.md?raw";
import { useAuditLog, type AuditEntry } from "./useData";

const CATEGORIES = ["security", "access", "data", "admin", "billing", "integration"];

export function AuditViewer() {
  const [params, setParams] = useListParams({ ordering: "-created_at" });
  const log = useAuditLog(params);
  const [open, setOpen] = useState<AuditEntry | null>(null);
  const columns: GridColDef<AuditEntry>[] = [
    { field: "created_at", headerName: "When", width: 150, renderCell: (p) => <DateText value={p.value as string} withTime /> },
    { field: "category", headerName: "Category", width: 110, renderCell: (p) => <Chip size="small" label={p.value as string} /> },
    { field: "summary", headerName: "What", flex: 1, minWidth: 220 },
    { field: "actor_email", headerName: "Who", width: 200, renderCell: (p) => <span>{p.value as string}{p.row.on_behalf_of ? ` (via ${p.row.on_behalf_of})` : ""}</span> },
    { field: "target_label", headerName: "Record", width: 180, valueGetter: (v, row) => (v as string) || row.target_id },
    { field: "action", headerName: "Action", width: 220 },
    { field: "ip_address", headerName: "IP", width: 130 },
  ];
  return (
    <>
      <PageHeader help={help} title="Audit log" subtitle="Security and change history for this organization" crumbs={[{ label: "Settings" }, { label: "Audit log" }]} />
      <DataTable viewKey="audit.log" columns={columns} data={log.data} isLoading={log.isLoading} params={params} onParamsChange={setParams}
        exportPath="/api/v1/audit/log/export/" onRowClick={setOpen}
        filters={<>
          <TextField select size="small" label="Category" value={String(params.category ?? "")} onChange={(e) => setParams({ ...params, category: e.target.value, page: 1 })} sx={{ width: 150 }}>
            <MenuItem value="">All</MenuItem>{CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Actor email" value={String(params.actor_email ?? "")} onChange={(e) => setParams({ ...params, actor_email: e.target.value, page: 1 })} sx={{ width: 200 }} />
          <TextField size="small" type="date" label="Since" InputLabelProps={{ shrink: true }} value={String(params.since ?? "").slice(0, 10)} onChange={(e) => setParams({ ...params, since: e.target.value ? `${e.target.value}T00:00:00Z` : "", page: 1 })} sx={{ width: 160 }} />
          <TextField size="small" type="date" label="Until" InputLabelProps={{ shrink: true }} value={String(params.until ?? "").slice(0, 10)} onChange={(e) => setParams({ ...params, until: e.target.value ? `${e.target.value}T23:59:59Z` : "", page: 1 })} sx={{ width: 160 }} />
        </>} />
      <Dialog open={!!open} onClose={() => setOpen(null)}>
        <DialogTitle>{open?.summary}</DialogTitle>
        <DialogContent>
          {open && (
            <Box sx={{ display: "grid", gap: 1 }}>
              <Typography variant="body2"><b>Action:</b> {open.action} · <b>Category:</b> {open.category}</Typography>
              <Typography variant="body2"><b>Actor:</b> {open.actor_email || "system"} {open.on_behalf_of && `(impersonated by ${open.on_behalf_of})`}</Typography>
              <Typography variant="body2"><b>Record:</b> {open.target_type} {open.target_label || open.target_id}</Typography>
              <Typography variant="body2"><b>From:</b> {open.ip_address ?? "—"} · {open.user_agent}</Typography>
              <Typography variant="body2"><b>Request:</b> {open.request_id || "—"}</Typography>
              <Typography variant="subtitle2">Changes</Typography>
              <Box component="pre" sx={{ m: 0, fontSize: 12, bgcolor: "action.hover", p: 1, borderRadius: 1 }}>{JSON.stringify(open.changes, null, 2)}</Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
